import cx from 'classnames';
import _ from 'lodash';
import React from 'react';
import { FormControl } from '../form-control';
import { Input } from '../input';
import { Radio } from '../radio';
import { Select } from '../select';
import { TextArea } from '../text-area/text-area';
import { Toggle } from '../toggle';
import { Typography } from '../typography';
import { Form } from './form';
import styles from './form-builder.module.scss';
import { ElementType } from './interface';
/**
 * @param id : id and name for the input
 * @param value : default value of the input
 * @param config : configuration for input control
 */
const renderInput = (id, value, config, onValueChanged) => {
    const { disabled, label, placeholder } = config;
    let inputControl = React.createElement(React.Fragment, null);
    const onChange = (event) => {
        let selectedId;
        let selectedValue;
        if (config.type === 'number') {
            selectedId = id;
            selectedValue = event;
        }
        else {
            selectedId = event.target.id.split('form-item-')[1];
            selectedValue = event.target.value;
        }
        onValueChanged(selectedId, selectedValue);
    };
    const onChangeFiles = (event) => {
        const files = event.target.files || [];
        const filesData = [];
        if (files.length) {
            for (let i = 0; i < files.length; i++) {
                filesData.push(files[i]);
            }
            const selectedFiles = filesData.length === 1 ? filesData[0] : filesData;
            onValueChanged(id, selectedFiles);
        }
    };
    switch (config.type) {
        case 'text':
            inputControl = (React.createElement(Input.Text, { type: "text", disabled: disabled, ariaLabel: _.isString(label) ? label : '', placeholder: placeholder, value: value, name: id, onChange: onChange, "data-testid": config.dataTestId, tabIndex: config.tabIndex }));
            break;
        case 'number':
            inputControl = (React.createElement(Input.Number, { disabled: disabled, ariaLabel: _.isString(label) ? label : '', placeholder: placeholder, value: value, name: id, onChange: onChange, allowSpinner: config.allowSpinner, "data-testid": config.dataTestId }));
            break;
        case 'textarea':
            inputControl = (React.createElement(TextArea, { value: value, disabled: disabled, placeholder: placeholder, name: id, ariaLabel: _.isString(label) ? label : '', onChange: onChange }));
            break;
        case 'file':
            inputControl = (React.createElement(Input.File, { disabled: disabled, placeholder: placeholder, value: value, name: id, onChange: onChangeFiles }));
            break;
        case 'password':
            inputControl = (React.createElement(Input.Text, { type: "password", disabled: disabled, ariaLabel: _.isString(label) ? label : '', placeholder: placeholder, value: value, name: id, onChange: onChange }));
            break;
        default:
            inputControl = React.createElement(React.Fragment, null, "Not Supported");
    }
    return inputControl;
};
const renderRadioGroup = (id, config, value, onValueChanged) => {
    let inputControl = React.createElement(React.Fragment, null);
    const { children, orientation = 'list' } = config;
    const onChange = (event) => {
        const newValue = _.get(event, 'target.value');
        onValueChanged(id, newValue);
    };
    const props = {
        id,
        key: id,
        selectedValue: value,
        onChange,
        orientation,
        containerClassName: styles.radioFieldset,
    };
    inputControl = (React.createElement(Radio.Group, { ...props }, children.map((radioItem) => {
        return (React.createElement(Radio, { key: radioItem.value, value: radioItem.value, label: radioItem.label }));
    })));
    return inputControl;
};
const renderSelect = (id, value, config, onValueChanged) => {
    const { disabled, placeholder, options, dropdownWidth, isSearchEnabled = false, footer, stickyFooter, } = config;
    let inputControl = React.createElement(React.Fragment, null);
    inputControl = (React.createElement(Select.Dropdown, { id: id, disabled: disabled, placeholder: placeholder, optionsGroups: options === undefined ? [] : options, dropdownWidth: dropdownWidth, selectedOptions: [value], isSearchEnabled: isSearchEnabled, footer: footer, stickyFooter: stickyFooter, onChange: (selectedValue) => {
            onValueChanged(id, selectedValue.id);
        }, "data-testid": config.dataTestId }));
    return inputControl;
};
const renderToggle = (id, value, valueLabels, config, onValueChanged) => {
    const { disabled, label } = config;
    let inputControl = React.createElement(React.Fragment, null);
    inputControl = (React.createElement(Toggle, { id: id, configLabel: _.isString(label) ? label : '', checked: typeof value === 'boolean' ? value : value === 'true', onChange: (event) => {
            onValueChanged(id, event.target.checked);
        }, name: id, label: valueLabels && valueLabels[value], disabled: disabled, labelPosition: "right" }));
    return inputControl;
};
const getValueForViewMode = ({ elementType, valueLabels, value, config, }) => {
    if (elementType === ElementType.TOGGLE) {
        return valueLabels[value];
    }
    if (elementType === ElementType.SELECT) {
        const { options } = config;
        const matchingOptions = options
            .flatMap((option) => option.options)
            .filter((option) => option.id === value);
        if (matchingOptions && matchingOptions.length === 1) {
            return matchingOptions[0].label;
        }
    }
    return value;
};
export const InputFactory = ({ id, elementType, config, value = '', valid = true, validation = 'NONE', onValueChanged, mode = 'edit', rowLayout = 'vertical', valueLabels, className, errorInsteadOfLabel = false, }) => {
    const getInputType = () => {
        let inputType = null;
        switch (elementType) {
            case ElementType.INPUT:
                inputType = renderInput(id, value, config, onValueChanged);
                break;
            case ElementType.SELECT:
                inputType = renderSelect(id, value, config, onValueChanged);
                break;
            case ElementType.TOGGLE:
                inputType = renderToggle(id, value, valueLabels, config, onValueChanged);
                break;
            case ElementType.RADIO:
                inputType = renderRadioGroup(id, config, value, onValueChanged);
                break;
            default:
                inputType = React.createElement(React.Fragment, null, "Not supported");
                break;
        }
        return inputType;
    };
    const renderDots = () => {
        return [1, 2, 3, 4, 5, 6].map((i) => (React.createElement("div", { key: i, className: styles.dot })));
    };
    const { noMargin } = config;
    const getInputTypeForViewMode = () => {
        const { isTextValueColorLight, type, wrapContent = true, } = config;
        if (elementType === ElementType.INPUT && type === 'password') {
            return React.createElement("div", { className: styles.dotWrapper }, renderDots());
        }
        return (React.createElement("div", null,
            React.createElement(Typography, { variant: "content-label-subhead", className: isTextValueColorLight
                    ? styles.isTextValueColorLight
                    : '', noMargin: noMargin, wrapContent: wrapContent }, getValueForViewMode({
                elementType,
                valueLabels,
                value,
                config,
            }))));
    };
    const formControlClassNames = cx(className, elementType === 'radio' ? styles.alignRadio : '');
    /** To return based on the control type
     *  we might not need the lable from form control as some form input
     *  already has the label associated with it example : toggle
     */
    const getFormControl = () => {
        return mode === 'edit' ? (React.createElement(FormControl, { label: config.label, hasError: !valid, layout: rowLayout, errorMessage: validation.error, className: formControlClassNames, helpText: config.helpText, disabled: config.disabled, errorInsteadOfLabel: errorInsteadOfLabel }, config.showShimmer ? (React.createElement("div", { className: config
                .customShimmerClassName })) : (getInputType()))) : (getInputTypeForViewMode());
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Form.Item, { type: mode, label: mode === 'view' ? config.label : '', rowLayout: rowLayout, noMargin: noMargin }, getFormControl())));
};
//# sourceMappingURL=input-factory.js.map